// ============================================================================
// AntiTamper — Deterrent layer untuk block F12 / view-source / inspect
// ============================================================================
// IMPORTANT (honest disclaimer):
//   Tidak ada cara 100% block DevTools di browser. Browser bisa:
//   - Disable JS via settings → semua deterrent JS gugur
//   - Pakai bookmarklet / browser extension
//   - View source via `view-source:` URL prefix (tergantung browser)
//   - Headless browser / curl → bypass UI sepenuhnya
//
// Layer ini = DETERRENT untuk casual user (90% kasus). Real security harus:
//   1. Server-side auth check (sudah: SecurityGate + /api/auth-check)
//   2. Sensitive data NEVER di client bundle (env vars di server)
//   3. Code obfuscation (Vite minify default)
//   4. RLS policies di DB (sudah)
//
// ADMIN BYPASS:
//   Tekan Ctrl+Alt+K → masukkan password admin → AntiTamper unlocked
//   sepanjang session (sampai user close tab atau Ctrl+Alt+L untuk re-lock).
//   Username + password admin diverifikasi server lewat
//   /api/auth-check. Stored di sessionStorage 'horus.devtools.unlocked'.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { ShieldAlert, Lock } from 'lucide-react';

const DETECT_INTERVAL_MS = 1000;
const DEVTOOLS_THRESHOLD_PX = 160;
const UNLOCK_KEY = 'horus.devtools.unlocked';

// Unlock diverifikasi server lewat /api/auth-check; tidak ada sandi di bundel klien.
// Hasil: true = lolos, false = ditolak, null = dibatalkan.
async function verifyUnlock() {
  const username = window.prompt('🔓 Admin Unlock — Masukkan username untuk bypass DevTools block:');
  if (!username) return null;
  const password = window.prompt('Masukkan password:');
  if (!password) return null;
  try {
    const res = await fetch('/api/auth-check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.trim(), password, level: 'root' }),
    });
    return res.ok;
  } catch { return false; }
}

function isUnlocked() {
  try {
    return sessionStorage.getItem(UNLOCK_KEY) === 'true';
  } catch { return false; }
}

function setUnlocked(value) {
  try {
    if (value) sessionStorage.setItem(UNLOCK_KEY, 'true');
    else sessionStorage.removeItem(UNLOCK_KEY);
  } catch {}
}

export default function AntiTamper({ children }) {
  const [devtoolsDetected, setDevtoolsDetected] = useState(false);
  const [unlocked, setUnlockedState] = useState(isUnlocked);

  useEffect(() => {
    const onKeyDown = async (e) => {
      // ── ADMIN UNLOCK COMBO: Ctrl+Alt+K ──────────────────────────────────
      if (e.ctrlKey && e.altKey && (e.keyCode === 75 || e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        const ok = await verifyUnlock();
        if (ok) {
          setUnlocked(true);
          setUnlockedState(true);
          setDevtoolsDetected(false);
          alert('✅ AntiTamper UNLOCKED untuk session ini.\n\nDevTools + right-click + Ctrl+U sekarang aktif.\n\nTekan Ctrl+Alt+L untuk re-lock.');
        } else if (ok === false) {
          alert('❌ Kredensial salah. AntiTamper tetap aktif.');
        }
        return false;
      }

      // ── ADMIN RE-LOCK COMBO: Ctrl+Alt+L ─────────────────────────────────
      if (e.ctrlKey && e.altKey && (e.keyCode === 76 || e.key === 'l' || e.key === 'L')) {
        e.preventDefault();
        setUnlocked(false);
        setUnlockedState(false);
        setDevtoolsDetected(false);
        alert('🔒 AntiTamper RE-LOCKED. DevTools / right-click di-block lagi.');
        return false;
      }

      // Kalau sudah unlocked, skip semua block
      if (unlocked) return;

      // ── BLOCK SHORTCUTS ──────────────────────────────────────────────────
      // F12
      if (e.keyCode === 123) { e.preventDefault(); return false; }
      // Ctrl+Shift+I (devtools), Ctrl+Shift+J (console), Ctrl+Shift+C (inspect)
      if (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67)) {
        e.preventDefault(); return false;
      }
      // Ctrl+U (view source)
      if (e.ctrlKey && e.keyCode === 85) { e.preventDefault(); return false; }
      // Ctrl+S (save page)
      if (e.ctrlKey && e.keyCode === 83) { e.preventDefault(); return false; }
    };

    // Block right-click context menu (kecuali sudah unlocked)
    const onContextMenu = (e) => {
      if (unlocked) return;
      e.preventDefault();
      return false;
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('contextmenu', onContextMenu);

    // DevTools detection via window dimension diff
    const detectInterval = setInterval(() => {
      if (unlocked) return; // skip detection kalau sudah unlocked
      const heightDiff = window.outerHeight - window.innerHeight;
      const widthDiff  = window.outerWidth  - window.innerWidth;
      const isOpen = heightDiff > DEVTOOLS_THRESHOLD_PX || widthDiff > DEVTOOLS_THRESHOLD_PX;
      if (isOpen !== devtoolsDetected) setDevtoolsDetected(isOpen);
    }, DETECT_INTERVAL_MS);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('contextmenu', onContextMenu);
      clearInterval(detectInterval);
    };
  }, [devtoolsDetected, unlocked]);

  // Klik handler untuk overlay unlock button
  const handleOverlayUnlock = async () => {
    const ok = await verifyUnlock();
    if (ok) {
      setUnlocked(true);
      setUnlockedState(true);
      setDevtoolsDetected(false);
    } else if (ok === false) {
      alert('❌ Kredensial salah.');
    }
  };

  return (
    <>
      {children}
      {devtoolsDetected && !unlocked && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(2,6,14,0.97)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 24,
            padding: 32,
            backdropFilter: 'blur(12px)',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 96, height: 96, borderRadius: '50%',
            background: 'rgba(255,45,85,0.12)',
            border: '2px solid rgba(255,45,85,0.6)',
          }}>
            <ShieldAlert size={48} color="#ff2d55" strokeWidth={2} />
          </div>
          <div style={{ textAlign: 'center', maxWidth: 480 }}>
            <p style={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: '0.2em',
              color: '#ff2d55',
              marginBottom: 12,
            }}>
              ⚠ AKSES TIDAK SAH TERDETEKSI
            </p>
            <h2 style={{
              fontFamily: 'Nunito, sans-serif',
              fontSize: 22,
              fontWeight: 800,
              color: '#e2e8f0',
              marginBottom: 16,
              lineHeight: 1.3,
            }}>
              Pemeriksa Browser Tidak Diizinkan
            </h2>
            <p style={{
              fontFamily: 'Nunito, sans-serif',
              fontSize: 13,
              color: '#8898ad',
              lineHeight: 1.6,
              marginBottom: 20,
            }}>
              Sistem mendeteksi upaya akses kode sumber atau Developer Tools.
              Akses ke HORUS V2 dibatasi untuk operator resmi PT Kartika Eka Satria.
              Tutup Developer Tools untuk melanjutkan.
            </p>

            {/* ADMIN UNLOCK BUTTON */}
            <button
              onClick={handleOverlayUnlock}
              style={{
                marginBottom: 16,
                padding: '10px 20px',
                background: 'rgba(0,212,255,0.12)',
                border: '1px solid rgba(0,212,255,0.4)',
                borderRadius: 8,
                color: '#00d4ff',
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.1em',
                cursor: 'pointer',
              }}
            >
              🔓 ADMIN UNLOCK (CTRL+ALT+K)
            </button>

            <p style={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: 10,
              color: '#6a7a90',
              letterSpacing: '0.05em',
              padding: '8px 12px',
              background: 'rgba(255,45,85,0.06)',
              border: '1px solid rgba(255,45,85,0.2)',
              borderRadius: 6,
              display: 'inline-block',
            }}>
              <Lock size={10} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
              Sesi audit · Insiden dicatat · Operator: {sessionStorage.getItem('horus.access.admin.token') ? 'AUTHENTICATED' : 'ANONYMOUS'}
            </p>
            <p style={{
              fontFamily: 'Nunito, sans-serif',
              fontSize: 10,
              color: '#4f6280',
              marginTop: 24,
            }}>
              English: Unauthorized access detected. Close Developer Tools to continue.
              <br />
              © 2026 KES (PT Kartika Eka Satria). All rights reserved.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
